import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaSearch, FaChevronDown, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import ProjectService, { Project, ProjectStatus, PROJECT_STATUS_OPTIONS } from '../../contacts/services/ProjectService';

export default function ProjectsListTab(): React.JSX.Element {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilters, setStatusFilters] = useState<ProjectStatus[]>([]);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      let data = await ProjectService.list({
        search: searchTerm || undefined,
      });

      // Filter by selected statuses (multi-select)
      if (statusFilters.length > 0) {
        data = data.filter((project) => statusFilters.includes(project.status));
      }

      // Filter by date range
      if (dateFrom || dateTo) {
        data = data.filter((project) => {
          const dateStr = project.updated_at;
          if (!dateStr) return false;
          const projectDate = new Date(dateStr);
          projectDate.setHours(0, 0, 0, 0);

          if (dateFrom) {
            const from = new Date(dateFrom);
            from.setHours(0, 0, 0, 0);
            if (projectDate < from) return false;
          }
          if (dateTo) {
            const to = new Date(dateTo);
            to.setHours(23, 59, 59, 999);
            if (projectDate > to) return false;
          }
          return true;
        });
      }

      setProjects(data);
    } catch (err) {
      console.error('Failed to load projects:', err);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilters, dateFrom, dateTo]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const handleSearch = () => {
    loadProjects();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleView = (project: Project) => {
    navigate(`/projects/${project.id}`);
  };

  const toggleStatusFilter = (status: ProjectStatus) => {
    setStatusFilters((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatLocation = (project: Project): string => {
    const parts = [project.postal_code, project.city].filter(Boolean);
    return parts.join(' ') || '-';
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE');
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(projects.map((p) => p.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (projectId: number, checked: boolean) => {
    const newSet = new Set(selectedIds);
    if (checked) {
      newSet.add(projectId);
    } else {
      newSet.delete(projectId);
    }
    setSelectedIds(newSet);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const confirmed = window.confirm(`${selectedIds.size} Projekt(e) wirklich löschen?`);
    if (!confirmed) return;

    setBulkDeleting(true);
    let successCount = 0;
    let failCount = 0;

    for (const id of Array.from(selectedIds)) {
      try {
        await ProjectService.delete(id);
        successCount++;
      } catch (err) {
        failCount++;
        console.error(`Failed to delete project ${id}:`, err);
      }
    }

    setBulkDeleting(false);
    setSelectedIds(new Set());

    if (successCount > 0) {
      toast.success(`${successCount} Projekt(e) gelöscht`);
    }
    if (failCount > 0) {
      toast.error(`${failCount} Projekt(e) konnten nicht gelöscht werden`);
    }

    loadProjects();
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
        }}
      >
        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#021a4a' }}>
          Projects ({projects.length})
        </h3>
      </div>

      {/* Filters */}
      <div
        style={{
          display: 'flex',
          gap: '16px',
          marginBottom: '24px',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
        }}
      >
        <div style={{ flex: '1', minWidth: '200px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: '#666' }}>
            Search
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Project name, reference..."
              style={{
                width: '100%',
                padding: '10px 12px 10px 38px',
                border: '1px solid #e0e3ef',
                borderRadius: '6px',
                fontSize: '14px',
              }}
            />
            <FaSearch
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#999',
              }}
            />
          </div>
        </div>

        <div style={{ minWidth: '220px', position: 'relative' }} ref={statusDropdownRef}>
          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: '#666' }}>
            Status
          </label>
          <button
            onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: statusFilters.length > 0 ? '1px solid #3b82f6' : '1px solid #e0e3ef',
              borderRadius: '6px',
              fontSize: '14px',
              background: statusFilters.length > 0 ? '#eff6ff' : 'white',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              textAlign: 'left',
            }}
          >
            <span style={{ color: statusFilters.length > 0 ? '#1d4ed8' : '#666' }}>
              {statusFilters.length === 0
                ? 'All statuses'
                : statusFilters.length === 1
                  ? ProjectService.getStatusLabel(statusFilters[0])
                  : `${statusFilters.length} selected`}
            </span>
            <FaChevronDown size={12} style={{ color: '#999', transform: statusDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
          </button>
          {statusDropdownOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: '4px',
                background: 'white',
                border: '1px solid #e0e3ef',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                padding: '12px',
                zIndex: 100,
                minWidth: '220px',
              }}
            >
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {PROJECT_STATUS_OPTIONS.map((opt) => {
                  const isActive = statusFilters.includes(opt.value);
                  const statusColor = ProjectService.getStatusColor(opt.value);
                  return (
                    <button
                      key={opt.value}
                      onClick={() => toggleStatusFilter(opt.value)}
                      style={{
                        padding: '7px 14px',
                        borderRadius: '16px',
                        fontSize: '13px',
                        fontWeight: 600,
                        border: `1px solid ${isActive ? statusColor.text : '#e0e3ef'}`,
                        background: 'white',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        color: isActive ? statusColor.text : '#666',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              {statusFilters.length > 0 && (
                <button
                  onClick={() => setStatusFilters([])}
                  style={{
                    marginTop: '10px',
                    padding: '6px 12px',
                    fontSize: '12px',
                    fontWeight: 500,
                    border: 'none',
                    background: 'transparent',
                    color: '#999',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  Clear all
                </button>
              )}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: '#666' }}>
              From
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={{
                padding: '10px 12px',
                border: '1px solid #e0e3ef',
                borderRadius: '6px',
                fontSize: '14px',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: '#666' }}>
              To
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              style={{
                padding: '10px 12px',
                border: '1px solid #e0e3ef',
                borderRadius: '6px',
                fontSize: '14px',
              }}
            />
          </div>
          {(dateFrom || dateTo) && (
            <button
              onClick={() => {
                setDateFrom('');
                setDateTo('');
              }}
              style={{
                padding: '10px 12px',
                border: 'none',
                background: 'transparent',
                color: '#999',
                fontSize: '13px',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '12px 16px',
            marginBottom: '16px',
            background: '#fef3c7',
            borderRadius: '8px',
            border: '1px solid #fcd34d',
          }}
        >
          <span style={{ fontWeight: 600, color: '#92400e' }}>
            {selectedIds.size} ausgewählt
          </span>
          <button
            onClick={handleBulkDelete}
            disabled={bulkDeleting}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '6px',
              background: '#dc2626',
              color: 'white',
              fontSize: '13px',
              fontWeight: 600,
              cursor: bulkDeleting ? 'wait' : 'pointer',
              opacity: bulkDeleting ? 0.7 : 1,
            }}
          >
            <FaTrash size={12} />
            {bulkDeleting ? 'Lösche...' : 'Löschen'}
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            style={{
              background: 'none',
              border: 'none',
              color: '#92400e',
              fontSize: '13px',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            Auswahl aufheben
          </button>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
          Loading...
        </div>
      ) : projects.length === 0 ? (
        <div
          style={{
            padding: '60px',
            textAlign: 'center',
            color: '#888',
            background: '#f8f9fc',
            borderRadius: '8px',
          }}
        >
          <p style={{ margin: 0, fontSize: '16px' }}>No projects found</p>
          <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
            Projects are created in the contact detail view of a customer.
          </p>
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8f9fc', borderBottom: '2px solid #e0e3ef' }}>
              <th style={{ ...thStyle, width: '40px', textAlign: 'center' }}>
                <input
                  type="checkbox"
                  checked={projects.length > 0 && selectedIds.size === projects.length}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                />
              </th>
              <th style={{ ...thStyle, width: '140px' }}>Nr.</th>
              <th style={thStyle}>Project</th>
              <th style={thStyle}>Customer</th>
              <th style={{ ...thStyle, width: '120px' }}>Status</th>
              <th style={thStyle}>Location</th>
              <th style={{ ...thStyle, width: '120px' }}>Updated</th>
              <th style={{ ...thStyle, width: '80px', textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {projects.map((project) => {
              const statusColor = ProjectService.getStatusColor(project.status);
              return (
                <tr
                  key={project.id}
                  style={{
                    borderBottom: '1px solid #e0e3ef',
                    cursor: 'pointer',
                    background: selectedIds.has(project.id) ? '#fefce8' : 'transparent',
                  }}
                  onClick={() => handleView(project)}
                >
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(project.id)}
                      onChange={(e) => handleSelectOne(project.id, e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                    />
                  </td>
                  <td style={{ ...tdStyle, color: '#666', fontSize: '13px', fontFamily: 'monospace' }}>
                    {project.reference || '-'}
                  </td>
                  <td style={tdStyle}>
                    <div>
                      <div style={{ fontWeight: 600, color: '#021a4a' }}>{project.name}</div>
                    </div>
                  </td>
                  <td style={tdStyle}>{project.partner_name || '-'}</td>
                  <td style={tdStyle}>
                    <span
                      style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 600,
                        background: statusColor.bg,
                        color: statusColor.text,
                      }}
                    >
                      {ProjectService.getStatusLabel(project.status)}
                    </span>
                  </td>
                  <td style={tdStyle}>{formatLocation(project)}</td>
                  <td style={tdStyle}>{formatDate(project.updated_at)}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleView(project);
                      }}
                      title="Details"
                      style={actionBtnStyle}
                    >
                      <FaEye size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: '12px 16px',
  textAlign: 'left',
  fontSize: '13px',
  fontWeight: 600,
  color: '#666',
};

const tdStyle: React.CSSProperties = {
  padding: '14px 16px',
  fontSize: '14px',
  color: '#333',
};

const actionBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: '6px',
  cursor: 'pointer',
  color: '#666',
  borderRadius: '4px',
};
